import React, { useState, useEffect, useMemo } from "react"
import { useNavigate }                          from "react-router-dom"
import { useDispatch, useSelector }             from "react-redux"
import {
  fetchLoginOptions,
  loginUser,
  clearLoginOptions,
  selectLoginStatus,
  selectLoginError,
  selectLoginOptions,
  selectUserName,
}                                               from "../../features/userSlice"
import "./LoginScreen.css"

/* ──────────────────────────────────────────────────────────────────
   LoginScreen — myERP
   ──────────────────────────────────────────────────────────────────
   Two-step flow:

     Step 1 (credentials):
       Employee ID + Password → "Continue" →
       fetchLoginOptions() → POST /api/entity/getLoginOptions
       Server validates & returns the user's authorized
       companies / plants / divisions / fin-years.

     Step 2 (context picker):
       Company → Plant → Division (cascading) + Fin Year.
       Defaults are server-suggested.
       "Sign In" → loginUser() → POST /api/users/login
       Token issued, redirect to /dashboard.
   ────────────────────────────────────────────────────────────────── */

const LoginScreen = () => {
  const navigate  = useNavigate()
  const dispatch  = useDispatch()

  const loginStatus  = useSelector(selectLoginStatus)
  const loginError   = useSelector(selectLoginError)
  const loginOptions = useSelector(selectLoginOptions)
  const userName     = useSelector(selectUserName)

  // Step 1 state
  const [empId,    setEmpId]    = useState("")
  const [password, setPassword] = useState("")
  const [remember, setRemember] = useState(true)

  // Step 2 state
  const [entityCode, setEntityCode] = useState("")
  const [divGroup,   setDivGroup]   = useState("")
  const [divCode,    setDivCode]    = useState("")
  const [finYr,      setFinYr]      = useState("")

  /* ── status flags ────────────────────────────────────────── */
  const optionsLoading = loginStatus === "options-loading"
  const loggingIn      = loginStatus === "loading"
  const onPickerStep   = loginStatus === "options-ready" && !!loginOptions
  const busy           = optionsLoading || loggingIn

  /* ── pre-fill defaults when options arrive ───────────────── */
  useEffect(() => {
    if (!loginOptions) return
    const d = loginOptions.defaults || {}
    setEntityCode(d.entity_code || loginOptions.companies?.[0]?.entity_code || "")
    setDivGroup(  d.div_group   || "")
    setDivCode(   d.div_code    || "")
    setFinYr(     d.fin_yr      ||
                  loginOptions.fin_years?.find(f => f.is_current)?.fin_yr ||
                  loginOptions.fin_years?.[0]?.fin_yr || "")
  }, [loginOptions])

  /* ── cascade filtering ──────────────────────────────────── */

  const plantsForEntity = useMemo(() => {
    if (!loginOptions) return []
    return loginOptions.plants.filter(p => p.entity_code === entityCode)
  }, [loginOptions, entityCode])

  const divisionsForPlant = useMemo(() => {
    if (!loginOptions) return []
    return loginOptions.divisions.filter(
      d => d.entity_code === entityCode && d.div_group === divGroup
    )
  }, [loginOptions, entityCode, divGroup])

  // Clamp plant/division when their parent changes
  useEffect(() => {
    if (!loginOptions) return
    if (plantsForEntity.length === 0) {
      setDivGroup(""); setDivCode(""); return
    }
    if (!plantsForEntity.find(p => p.div_group === divGroup)) {
      setDivGroup(plantsForEntity[0].div_group)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityCode, loginOptions])

  /*
  useEffect(() => {
    if (!loginOptions) return
    if (divisionsForPlant.length === 0) {
      setDivCode(""); return
    }
    if (!divisionsForPlant.find(d => d.div_code === divCode)) {
      setDivCode(divisionsForPlant[0].div_code)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divGroup, loginOptions])
  */
  useEffect(() => {
    if (!loginOptions) return
    if (divisionsForPlant.length === 0) {
      setDivCode(""); return
    }
    // Blank ("") is legitimate when 2+ divisions available — don't clamp.
    if (divCode === "") return
    // Otherwise, clamp to the first authorized division if current is no longer valid.
    if (!divisionsForPlant.find(d => d.div_code === divCode)) {
      setDivCode(divisionsForPlant[0].div_code)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divGroup, loginOptions])

  /* ── handlers ────────────────────────────────────────────── */

  const handleStep1 = async (e) => {
    e.preventDefault()
    if (!empId.trim() || !password) return
    await dispatch(fetchLoginOptions({
      emp_id: empId.trim(),
      password,
    }))
    // Rejected → error renders below.
    // Fulfilled → status "options-ready" → picker step renders.
  }

  const handleStep2 = async (e) => {
    e.preventDefault()
    if (!entityCode || !divGroup || !finYr) return

    const result = await dispatch(loginUser({
      emp_id:      empId.trim(),
      password,
      entity_code: entityCode,
      div_group:   divGroup,
      div_code:    divCode || null,
      fin_yr:      finYr,
    }))

    if (loginUser.fulfilled.match(result)) {
      setPassword("")     // clear from local state
      navigate("/dashboard", { replace: true })
    }
  }

  const handleBack = () => {
    dispatch(clearLoginOptions())
    setPassword("")
    setEntityCode(""); setDivGroup(""); setDivCode(""); setFinYr("")
  }

  /* ── render ──────────────────────────────────────────────── */

  return (
    <div className="lg-shell">

      {/* ── LEFT PANEL (unchanged) ── */}
      <div className="lg-left">
        <button className="lg-back" onClick={() => navigate("/")}>
          ← Back
        </button>

        <div className="lg-logo">
          <div className="lg-logo-box">S</div>
          <div>
            <div className="lg-brand"><em>Singhal</em><span className="lg-brand-erp">ERP</span></div>            
            <div className="lg-version">
              Singhal Group of Industries · v4.0
            </div>
          </div>
        </div>

        <div className="lg-tagline">
          <strong>One Platform.<br />Total Control.</strong>
          <p>
            Integrated ERP for Finance, Inventory,
            <br />Sales, Production &amp; HR
          </p>
        </div>

        <ul className="lg-features">
          <li>Oracle 11g connection pooling — blazing fast</li>
          <li>GST / TDS compliance built-in</li>
          <li>Multi-company, multi-branch support</li>
          <li>SAP-style MDI workspace</li>
          <li>Role-based menu &amp; field security</li>
        </ul>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="lg-right">
        <div className="lg-form-box">

          {!onPickerStep ? (
            /* ═══════════════ STEP 1 — CREDENTIALS ═══════════════ */
            <>
              <h2 className="lg-title">Welcome back</h2>
              <p className="lg-subtitle">
                Sign in to your SinghalERP workspace
              </p>

              {loginError && !optionsLoading && (
                <div className="lg-error">
                  {typeof loginError === "string"
                    ? loginError
                    : "Invalid Employee ID or Password"}
                </div>
              )}

              <form onSubmit={handleStep1} autoComplete="on">
                <div className="lg-field">
                  <label className="lg-label">Employee ID</label>
                  <input
                    className="lg-input"
                    type="text"
                    value={empId}
                    onChange={(e) => setEmpId(e.target.value)}
                    placeholder="Enter your employee ID"
                    autoComplete="username"
                    autoFocus
                    disabled={busy}
                  />
                </div>

                <div className="lg-field">
                  <label className="lg-label">Password</label>
                  <input
                    className="lg-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={busy}
                  />
                </div>

                <div className="lg-row">
                  <label className="lg-remember">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                    />
                    Remember me
                  </label>
                  <button
                    type="button"
                    className="lg-forgot"
                    onClick={() => alert("Contact EDP for password reset")}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  className={`lg-submit ${optionsLoading ? "loading" : ""}`}
                  disabled={busy || !empId.trim() || !password}
                >
                  {optionsLoading
                    ? <span className="lg-spinner">Authenticating…</span>
                    : "Continue"}
                </button>
              </form>
            </>
          ) : (
            /* ═══════════════ STEP 2 — CONTEXT PICKER ═══════════════ */
            <>
              <h2 className="lg-title">
                Hi {userName?.split(" ")[0] || "there"}
              </h2>
              <p className="lg-subtitle">
                Select your workspace context
              </p>

              {loginError && (
                <div className="lg-error">
                  {typeof loginError === "string"
                    ? loginError
                    : "Login failed"}
                </div>
              )}

              <form onSubmit={handleStep2} autoComplete="off">

                <div className="lg-field">
                  <label className="lg-label">Company</label>
                  <select
                    className="lg-select"
                    value={entityCode}
                    onChange={(e) => setEntityCode(e.target.value)}
                    disabled={busy}
                  >
                    {loginOptions.companies.map(c => (
                      <option key={c.entity_code} value={c.entity_code}>
                        {c.entity_name} ({c.entity_code})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="lg-field">
                  <label className="lg-label">Plant</label>
                  <select
                    className="lg-select"
                    value={divGroup}
                    onChange={(e) => setDivGroup(e.target.value)}
                    disabled={busy || plantsForEntity.length === 0}
                  >
                    {plantsForEntity.length === 0 && (
                      <option value="">No plants available</option>
                    )}
                    {plantsForEntity.map(p => (
                      <option key={p.div_group} value={p.div_group}>
                        {p.div_group_name} ({p.div_group})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="lg-field">
                  <label className="lg-label">Division</label>
                  <select
                    className="lg-select"
                    value={divCode}
                    onChange={(e) => setDivCode(e.target.value)}
                    disabled={busy || divisionsForPlant.length === 0}
                  >
                    {divisionsForPlant.length === 0 && (
                      <option value="">No divisions available</option>
                    )}
                    {divisionsForPlant.length > 1 && (
                      <option value="">(All divisions)</option>
                    )}
                    {divisionsForPlant.map(d => (
                      <option key={d.div_code} value={d.div_code}>
                        {d.div_name} ({d.div_code})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="lg-field">
                  <label className="lg-label">Financial Year</label>
                  <select
                    className="lg-select"
                    value={finYr}
                    onChange={(e) => setFinYr(e.target.value)}
                    disabled={busy}
                  >
                    {loginOptions.fin_years.map(f => (
                      <option key={f.fin_yr} value={f.fin_yr}>
                        FY {f.fin_yr}{f.is_current ? "  (current)" : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="lg-row">
                  <button
                    type="button"
                    className="lg-forgot"
                    onClick={handleBack}
                    disabled={busy}
                  >
                    ← Use different account
                  </button>
                </div>

                <button
                  type="submit"
                  className={`lg-submit ${loggingIn ? "loading" : ""}`}
                  disabled={busy || !entityCode || !divGroup || !finYr}
                >
                  {loggingIn
                    ? <span className="lg-spinner">Signing in…</span>
                    : "Sign In"}
                </button>
              </form>
            </>
          )}

          <div className="lg-footer">
            © 2026 Singhal Group · SinghalERP v4.0
            · Support: 1800-xxx-xxxx
          </div>

        </div>
      </div>
    </div>
  )
}

export default LoginScreen
